import {
  Avatar,
  Box,
  Flex,
  FormControl,
  FormLabel,
  Heading,
  HStack,
  Text,
  VStack,
} from "@chakra-ui/react";
import { zodResolver } from "@hookform/resolvers/zod";
import { Helmet } from "react-helmet-async";
import { FormProvider, useForm } from "react-hook-form";
import { useNavigate } from "react-router";

import FormInput from "@/common/components/Form/FormInput";
import { FormRootError } from "@/common/components/Form/FormRootError";
import FormSubmit from "@/common/components/Form/FormSubmit";
import InfoIconTooltip from "@/common/components/InfoIconTooltip";
import SimpleSelect from "@/common/components/SimpleSelect";
import { useEnvironmentUnsafe } from "@/common/contexts/EnvironmentContext";
import { useCreateAppMutation } from "@/common/data/useCreateAppMutation";
import { useFormMutationSubmitHandler } from "@/common/hooks/useApiForm";
import { CreateAppArgsSchema } from "@reflag/shared/appAPI";
import { EnvironmentUrl } from "@reflag/shared/urls";

const formSchema = CreateAppArgsSchema.pick({
  name: true,
});

function Form() {
  const navigate = useNavigate();
  const { org } = useEnvironmentUnsafe() ?? {};
  const createApp = useCreateAppMutation(org?.id);

  const form = useForm({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: "",
    },
  });

  const handleSubmit = useFormMutationSubmitHandler(
    form,
    createApp,
    async ({ environments }) => {
      navigate(EnvironmentUrl(environments[0]));
    },
    {
      prepareVariables: (data) => {
        if (!org) {
          throw new Error("Organization not found");
        }

        return {
          name: data.name,
        };
      },
    },
  );

  return (
    <form onSubmit={handleSubmit}>
      <FormProvider {...form}>
        <VStack align="flex-start" spacing={6}>
          <FormControl>
            <FormLabel fontSize="lg">Organization</FormLabel>
            <SimpleSelect
              options={
                org !== undefined
                  ? [
                      {
                        label: org.name,
                        value: org.id,
                        icon: (
                          <Avatar
                            name={org.name || org.id}
                            size="2xs"
                            src={org.logoUrl ?? undefined}
                          />
                        ),
                      },
                    ]
                  : []
              }
              size="lg"
              value={org?.id}
              w="full"
              isDisabled
              onChange={() => {}}
            />
          </FormControl>

          <Box w="100%">
            <FormInput
              autoComplete="off"
              label="Name"
              name="name"
              placeholder={`${org?.name} Testing`}
              size="lg"
            />

            <HStack color="gray.500" mt={2}>
              <Text fontSize="sm" maxW="md">
                You can create more apps later
              </Text>
              <InfoIconTooltip
                text="Create apps for each of your
            applications (web, mobile), environments (prod, dev) or products (consumer,
            supplier) in your organization."
              />
            </HStack>
          </Box>

          <FormRootError />

          <FormSubmit loadingText="Creating..." size="lg" disableOnSuccess>
            Create
          </FormSubmit>
        </VStack>
      </FormProvider>
    </form>
  );
}

export default function NewApp() {
  return (
    <>
      <Helmet>
        <title>Create New App</title>
      </Helmet>
      <Flex direction="column" w="compactForm">
        <Heading fontSize="3xl" mb={8}>
          Create app
        </Heading>
        <Form />
      </Flex>
    </>
  );
}
